import { type Space } from "@prisma/client";

import { cn, currency } from "~/lib/utils";

import { InfoTooltip } from "./InfoTooltip";

export function SpaceCard({ space, onClick, children }: { space: Space }) {
    const { price } = space;

    const section = space.section?.length === 1 ? `Section ${space.section}` : space.section;

    return (
        <div
            className={cn(
                "flex flex-col gap-2 bg-gray-150 rounded-lg h-full",
                onClick ? "cursor-pointer hover:bg-gray-200/50" : null,
            )}
            onClick={onClick}
        >
            <h2 className="flex flex-col items-center gap-2 px-4 lg:px-6 pt-4 lg:pt-6 pb-4">
                <SpaceImage src={space.thumbnail} size={onClick ? "md" : "lg"} />
            </h2>
            <div className="flex flex-col gap-2 px-4 lg:px-6 pb-4 lg:pb-6 h-full">
                <section className="flex justify-between w-full capitalize text-lg font-medium">
                    <h2 className="font-semibold">{section}</h2>
                    <h2 className="font-light">{currency(price)}</h2>
                </section>
                <section className="flex justify-between items-center">
                    <section className="flex gap-2">
                        <h2>Interment Rights</h2>
                        <InfoTooltip
                            content="Interment rights, also known as burial rights, are the right to direct the burial, entombment, or inurnment of human remains in a cemetery.
One internment right is the right to bury a single person. Two internment rights allow you place the remains of two individuals in one space and so on."
                        />
                    </section>
                    <div className="px-2 py-1.5 font-bold rounded text-sm">{space.intermentRights.length}</div>
                </section>
                {space.doubleCremationOnly && (
                    <section className="flex gap-2">
                        <h2 className="font-semibold">Double Cremation Only</h2>
                        <InfoTooltip
                            content="In spaces marked as “Double Cremation Only,” only two sets of cremated remains may be interred — no traditional burials are allowed in these spaces."
                        />
                    </section>
                )}
                <div className="flex justify-between items-center mt-auto">
                    <p>
                        <SpaceLocation {...space} />
                    </p>
                    <div className="bg-gray-200 px-2 py-1.5 font-bold rounded text-sm">{space.propertyType}</div>
                </div>
                {children}
            </div>
        </div>
    );
}

export function SpaceImage({
    src,
    className,
    size = "md",
}: {
    src: string;
    className?: string;
    size?: "md" | "lg" | "sm";
}) {
    return (
        <div
            className={cn(
                "w-full bg-gray-400/40 rounded-sm flex items-center justify-center text-center",
                className,
                size === "sm" && "h-32",
                size === "md" && "h-32 md:h-48",
                size === "lg" && "h-64 md:h-96",
            )}
        >
            {src ? <img src={src} className="w-full h-full object-cover overflow-hidden" alt="Memorial space" /> : null}
        </div>
    );
}

export function SpaceLocation({
    lotLotNbrNum,
    lotSpaceNbr,
    lotRowNbrAlpha,
    subSection,
}: Pick<Space, "lotLotNbrNum" | "lotSpaceNbr" | "lotRowNbrAlpha" | "subSection">) {
    return subSection ? (
        <span className="text-sm">
            <strong>{`${lotRowNbrAlpha}-${lotSpaceNbr}`}</strong>
            {`, ${subSection}`}
        </span>
    ) : (
        <>
            {/* {lotRowNbrAlpha ? <span>{lotRowNbrAlpha} </span> : null} */}
            {lotLotNbrNum ? <span>Lot-{lotLotNbrNum} </span> : null}
            <span>
                {lotRowNbrAlpha || "Space"}-{lotSpaceNbr}
            </span>
        </>
    );
}

export function getSpaceLocation(space: Space) {
    const { lotLotNbrNum, lotSpaceNbr, lotRowNbrAlpha, subSection } = space;
    if (subSection) {
        return `${lotRowNbrAlpha}-${lotSpaceNbr}, ${subSection}`;
    }
    return `${lotLotNbrNum ? `Lot-${lotLotNbrNum} ` : ""}${lotRowNbrAlpha || "Space"}-${lotSpaceNbr}`;
}
